import * as Sentry from "@sentry/react";
import { SnackbarContent } from "components/global/snackbar/SnackbarContent";
import { NonRfidFinishStep } from "components/orderSteps/nonRfid/NonRfidFinishStep";
import { NonRfidResetStep } from "components/orderSteps/nonRfid/NonRfidResetStep";
import { NonRfidTakeStep } from "components/orderSteps/nonRfid/NonRfidTakeStep";
import { OrderStep1 } from "components/orderSteps/OrderStep1";
import { OrderStep2 } from "components/orderSteps/OrderStep2";
import { OrderStep3 } from "components/orderSteps/OrderStep3";
import { OrderStep5 } from "components/orderSteps/OrderStep5";
import { RefillSummaryStep } from "components/orderSteps/RefillSummaryStep";
import { useDeviceContext } from "contexts/DeviceContext";
import { useSnackbarContext } from "contexts/SnackbarContext";
import { useCallback, useEffect, useRef } from "react";
import { useIdleTimerContext } from "react-idle-timer";
import {
	selectActiveLocalTransaction,
	selectCurrentOrderStep,
	selectFinishedLocalRefillId,
	selectRefillMode,
	selectTransactionDishes,
	updateCurrentOrderStep,
} from "slices/transactionSlice";
import { DoorState, LockState, TransactionMode, TransactionState } from "types";
import { isNonRfidFlow } from "utils/nonRfidFlow";
import { selectHasDoorBeenOpened, setHasDoorBeenOpenedDuringOrder } from "../slices/doorSlice";
import { useNonRfidStage } from "./useNonRfidStage";
import { useOrder } from "./useOrder";
import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

export const useOrderSteps = ({ open, toggleDrawer }: { open: boolean; toggleDrawer: (newState: boolean) => void }) => {
	const dispatch = useAppDispatch();
	// Active customer order or a local refill (refill card swiped at the fridge). Both drive
	// the same 2/3/5 transitions; remote refills are handled by RemoteRefillModal and excluded.
	const activeTransaction = useAppSelector(selectActiveLocalTransaction);
	const _orderDishes = useAppSelector(selectTransactionDishes);
	const currentOrderStep = useAppSelector(selectCurrentOrderStep);
	const hasDoorBeenOpened = useAppSelector(selectHasDoorBeenOpened);
	const { finishOrder } = useOrder();
	const { door } = useDeviceContext();
	const { openSnackbarWithContent } = useSnackbarContext();
	const idleTimer = useIdleTimerContext();
	const refillMode = useAppSelector(selectRefillMode);
	const finishedLocalRefillId = useAppSelector(selectFinishedLocalRefillId);
	const isRemoteRefill = refillMode === TransactionMode.Remote;
	const { lock, state: doors } = door;
	// Non-RFID prototype (`?flow=nonRfid`): same step machine, different drawer content per stage.
	const nonRfidStage = useNonRfidStage();

	const resetOrder = useCallback(() => {
		finishOrder();
	}, [finishOrder]);

	const hasSentDoorOpenedWithoutOrderRef = useRef(false);

	useEffect(() => {
		const isClosedAndUnlocked = lock.state === LockState.Unlocked && doors === DoorState.Closed;
		if (activeTransaction) {
			hasSentDoorOpenedWithoutOrderRef.current = false;

			// "Nothing taken yet": an RFID fridge reports it as an empty `removed` ledger. A non-RFID fridge sends
			// no inventory at all, so there the only signal is an ACTIVE order whose door has not been opened yet.
			// (ACTIVE matters: an order cancelled before the lock re-locks must fall through to FINISHED below.)
			const isBeforeDoorOpened = isNonRfidFlow()
				? activeTransaction.state === TransactionState.Active && !hasDoorBeenOpened
				: activeTransaction.inventory?.removed?.length === 0;
			if (isClosedAndUnlocked && isBeforeDoorOpened) {
				// If user has not interacted with the tablet, we activate the idle timer,
				// so the lights are turned on
				if (idleTimer.isIdle()) {
					idleTimer.activate();
				}
				dispatch(updateCurrentOrderStep(2));
			} else if (doors === DoorState.Opened && activeTransaction.state === TransactionState.Active) {
				// Set the flag to true, so we know that the fridge door has been opened
				dispatch(setHasDoorBeenOpenedDuringOrder(true));
				dispatch(updateCurrentOrderStep(3));
			} else if (activeTransaction.state === TransactionState.StockCheck) {
				// If the user opened the door, we show stock check screen,
				// otherwise we finish the order and return to the first step
				if (!hasDoorBeenOpened) {
					if (!isRemoteRefill) {
						openSnackbarWithContent(<SnackbarContent variant="orderNotStarted" user={activeTransaction.user} />);
					}
					dispatch(updateCurrentOrderStep(5));
				}
			} else if (activeTransaction.state === TransactionState.Finished && currentOrderStep !== 5) {
				// If the user opened the door, we show order summary screen, the order will have
				// this state even when user did not open the door, in that case, we do not show Order5
				dispatch(updateCurrentOrderStep(5));
			}
		} else {
			dispatch(updateCurrentOrderStep(1));
			dispatch(setHasDoorBeenOpenedDuringOrder(false));

			if (doors === DoorState.Opened && !hasSentDoorOpenedWithoutOrderRef.current) {
				hasSentDoorOpenedWithoutOrderRef.current = true;
				Sentry.captureException(new Error("Door opened without order"));
			}
		}
	}, [
		// activeTransaction.state and .inventory are intentionally not listed: under RTK/Immer,
		// any nested reducer change produces a new parent reference, so activeTransaction
		// reference change already covers nested-field changes.
		activeTransaction,
		doors,
		lock,
		hasDoorBeenOpened,
		idleTimer.isIdle,
		openSnackbarWithContent,
		currentOrderStep,
		dispatch,
		isRemoteRefill,
		idleTimer.activate,
	]);

	if (isNonRfidFlow()) {
		switch (nonRfidStage) {
			case "open":
				return <OrderStep2 open={open} toggleDrawer={toggleDrawer} />;
			case "take":
				return <NonRfidTakeStep open={open} toggleDrawer={toggleDrawer} />;
			case "finish":
				return <NonRfidFinishStep open={open} toggleDrawer={toggleDrawer} />;
			case "reset":
				return <NonRfidResetStep resetOrder={resetOrder} />;
			default:
				return <OrderStep1 open={open} toggleDrawer={toggleDrawer} variant="qrOnly" />;
		}
	}

	switch (currentOrderStep) {
		case 1:
			return <OrderStep1 open={open} toggleDrawer={toggleDrawer} />;
		case 2:
			return <OrderStep2 open={open} toggleDrawer={toggleDrawer} />;
		case 3:
		case 4:
			return <OrderStep3 open={open} toggleDrawer={toggleDrawer} />;
		case 5:
			// A finished LOCAL refill gets the refill summary instead of the order summary (JID-2360).
			// Its confirm button and 60 s countdown live in DrawerActionBar, like the order's back-to-home.
			return finishedLocalRefillId ? (
				<RefillSummaryStep open={open} toggleDrawer={toggleDrawer} />
			) : (
				<OrderStep5
					open={open}
					resetOrder={resetOrder}
					toggleDrawer={toggleDrawer}
					doorHasBeenOpened={hasDoorBeenOpened}
				/>
			);
		default:
			return <OrderStep1 open={true} toggleDrawer={toggleDrawer} />;
	}
};
